import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { ReportApis } from 'apis/ReportApis';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import usePagination from 'hooks/usePagination';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { convertDateToBE, generateUUID } from 'utils/constants/functions';
import FilterCustomerRevenue from './FilterCustomerRevenue';
import TableCustomerRevenue from './TableCustomerRevenue';

const CustomerGroupRevenuePage = () => {
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any>({});
  const [isShow, setIsShow] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const { page, page_size } = usePagination();
  const navigate = useNavigate();
  const ref = useRef<any>(null);

  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    const startDate = dayjs().startOf('month');
    const endDate = dayjs();

    if (user?.plantCode === '5000') return { year: [startDate, endDate] };

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id || 'SALE_ID',
        teamSalesSubId: teamSale?.subId || 'SALE_SUB_ID',
        teamSalesName: teamSale?.name,
        year: [startDate, endDate],
      };
    }

    return {
      plantId: userAny?.plantCode,
      year: [startDate, endDate],
    };
  }, [user]);

  const { mutate: getCustomerRevenue, isPending: getCustomerRevenueLoading } =
    useMutation({
      mutationFn: ReportApis.getCustomerGroupRevenue,
      onSuccess: (data: any, variables: any) => {
        const result = data?.data?.getCustomerDataReport || {};
        const items = (result.items || []).map((item: any) => ({
          ...item,
          id: generateUUID(),
        }));

        if (variables.type === 'export') {
          const items = result.items || [];

          return exportExcel_v2(
            variables.headerRow,
            items,
            'Báo cáo - Doanh thu theo nhóm Khách hàng',
          );
        }

        setData({
          ...result,
          items,
        });
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleFilter = (values: any) => {
    setIsShow(true);
    setFilters(values);
    navigate(`?page=1&page_size=${page_size}`);
  };

  const handleCancel = () => {
    const startDate = dayjs().startOf('month');
    const endDate = dayjs().endOf('month');

    setIsShow(false);
    setFilters({ year: [startDate, endDate] });
  };

  const getParamsForCustomerRevenue = useCallback(
    (skip: number, columns?: any) => {
      const startDate = filters.year[0];
      const endDate = filters.year[1];

      const startDateFormatted = convertDateToBE(startDate);
      const endDateFormatted = convertDateToBE(
        endDate > dayjs() ? dayjs() : endDate,
      );

      return {
        skip,
        take: columns ? data.pageInfo?.totalItems || 1000 : page_size,
        ...filters,
        plantId: filters.plantId || defaultFilter.plantId,
        fromDate: startDateFormatted,
        toDate: endDateFormatted,
        ...(columns && {
          type: 'export',
          headerRow: columns,
        }),
      };
    },
    [data.pageInfo?.totalItems, defaultFilter.plantId, filters, page_size],
  );

  const exportExcel = (columns: any) => {
    getCustomerRevenue(getParamsForCustomerRevenue(0, columns));
  };

  const handleExportExcel = () => {
    if (!ref.current) return;

    ref.current.handleExportExcel();
  };

  useEffect(() => {
    setTitle('Doanh thu theo nhóm Khách hàng');
    setBreadcrumb([
      { title: 'Báo cáo' },
      { title: 'Doanh thu theo nhóm Khách hàng' },
    ]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    if (!isShow) return;

    getCustomerRevenue(getParamsForCustomerRevenue((page - 1) * page_size));
  }, [
    defaultFilter.plantId,
    filters,
    filters.plantId,
    filters.year,
    getCustomerRevenue,
    getParamsForCustomerRevenue,
    isShow,
    page,
    page_size,
  ]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonExport disabled={!isShow} onClick={handleExportExcel} />
        </Col>
      </Row>
      <FilterCustomerRevenue
        onFilter={handleFilter}
        onCancel={handleCancel}
        defaultFilter={defaultFilter}
      />

      {isShow && (
        <TableCustomerRevenue
          ref={ref}
          exportExcel={exportExcel}
          loading={getCustomerRevenueLoading}
          data={data}
        />
      )}
    </>
  );
};

export default CustomerGroupRevenuePage;
